import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email');
      return;
    }

    setLoading(true);
    try {
      await api.post('/auth/forgot-password', { email });
      setSent(true);
      toast.success('Reset link sent! Check your email.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-[#1A1A1A] border-white/10">
        <CardHeader className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#00E676]/20 flex items-center justify-center">
            {sent ? (
              <CheckCircle className="w-8 h-8 text-[#00E676]" />
            ) : (
              <Mail className="w-8 h-8 text-[#00E676]" />
            )}
          </div>
          <CardTitle className="text-2xl text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {sent ? 'Check Your Email' : 'Forgot Password?'}
          </CardTitle>
          <p className="text-gray-400 mt-2">
            {sent
              ? 'We sent a password reset link to your email address.'
              : "No worries! Enter your email and we'll send you a reset link."}
          </p>
        </CardHeader>
        <CardContent>
          {sent ? (
            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-[#00E676]/10 border border-[#00E676]/20 text-center">
                <p className="text-[#00E676]">{email}</p>
              </div>
              <p className="text-sm text-gray-400 text-center">
                Didn't receive the email? Check your spam folder or{' '}
                <button
                  onClick={() => setSent(false)}
                  className="text-[#00E676] hover:underline"
                >
                  try again
                </button>
              </p>
              <Link to="/login">
                <Button variant="outline" className="w-full btn-secondary">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Login
                </Button>
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm text-gray-400">Email Address</label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="input-dark"
                  data-testid="forgot-email"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full btn-primary"
                data-testid="forgot-submit"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                ) : (
                  'Send Reset Link'
                )}
              </Button>
              <Link to="/login">
                <Button variant="ghost" className="w-full text-gray-400 hover:text-white">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Login
                </Button>
              </Link>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
